import type {
  FormattedUser,
  UserMenuEvent,
  UserRoleAttributes
} from '@library/types/user';
import {
  PlayerImage,
  PlayerName,
  PlayerProfile,
  PlayerSubheader
} from '@library/components/player';
import { DateTime } from 'luxon';
import { StatusIndicator } from '@library/components/StatusIndicator';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import { UserKebabMenu } from '@library/components/menus/kebabMenu/UserKebabMenu';
import { formatUserDetails } from '@bo-utils/user';
import { formatWithTimezoneChange } from '@library/utils/dateTime';

const RolesCell = ({ roles }: { roles: Array<UserRoleAttributes> }) => {
  if (!roles.length) return null;

  const rolesFirstTwo = roles.slice(0, 2);
  const rolesTooltip =
    roles.length > 2 ? roles.slice(2).map((role) => role.name) : undefined;

  return (
    <>
      {rolesFirstTwo.map((role) => (
        <span
          key={role.role_id}
          className="bg-soFresh-130 text-soClean-130 mx-1 whitespace-nowrap rounded-2xl px-3 py-2 leading-tight first:ml-0"
        >
          {role.name}
        </span>
      ))}
      {rolesTooltip && (
        <Tooltip text={rolesTooltip}>
          <span className="text-soClean-180 mx-1 cursor-default">
            +{roles.length - 2} more
          </span>
        </Tooltip>
      )}
    </>
  );
};

export interface UserRolesContentProps {
  user: FormattedUser;
  onUserMenuEvent: ({ userId, eventType }: UserMenuEvent) => void;
}

export const UserRolesRow = ({
  user,
  onUserMenuEvent
}: UserRolesContentProps) => {
  const { avatar, createdAt, email, id, initials, name, roles, status } =
    formatUserDetails(user);

  const formattedTime = formatWithTimezoneChange(
    DateTime.fromISO(createdAt),
    'MMM d, yyyy'
  );

  const userMenuActions = {
    onEdit: () => onUserMenuEvent({ userId: id, eventType: 'edit' }),
    onDelete: () => onUserMenuEvent({ userId: id, eventType: 'deactivate' })
  };

  return (
    <div className="border-soFresh-140 text-soClean-110 hover:bg-soFresh-120 group/user text-2xs relative flex items-center border-b xl:text-xs">
      <PlayerProfile
        className="flex flex-1 items-center gap-4 p-3"
        name={name}
        imageUrl={avatar}
        subheader={email}
      >
        {avatar ? (
          <PlayerImage className="bg-soFresh-120 size-10 rounded-lg" />
        ) : (
          <div className="bg-stankonia-100 text-soClean-100 flex size-10 min-w-[40px] items-center justify-center rounded-lg">
            <span className="text-xl uppercase antialiased" data-testid={email}>
              {initials}
            </span>
          </div>
        )}
        <div className="flex flex-col">
          <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium capitalize tracking-tight" />
          <PlayerSubheader className="text-soClean-170 font-heebo block text-xs font-medium tracking-tight" />
        </div>
      </PlayerProfile>

      <div className="flex flex-1 flex-wrap items-center">
        <RolesCell roles={roles} />
      </div>

      <p className="w-1/5 p-3">{formattedTime}</p>

      <div className="flex w-1/5 p-3 capitalize">
        <StatusIndicator className="self-center" status={status} />
        <p className="pl-2">{status}</p>
      </div>

      <div className="absolute right-2">
        <UserKebabMenu menuActions={userMenuActions} id={id} />
      </div>
    </div>
  );
};
